import { getMockMarketProfile } from "../market";

import { createId, createPastTimestamp, pickRandom, randomInt } from "./base";

import {
  AdAccountStatus,
  type AdAccount,
  type AdInsights,
  type AdWithInsights,
} from "@/features/ads/types";

const MARKET = getMockMarketProfile();
const BOTOX_ADSET = `Botox - ${MARKET.city} 25-45`;
const LASER_ADSET = `Laser - ${MARKET.city} 25-45`;
const COLLAB_ADSET = `Collab - Broad ${MARKET.region}`;

const CONVERSION_TYPES: AdWithInsights["conversionType"][] = [
  "message",
  "message",
  "form",
  "website",
];

const AD_SEEDS: Array<{
  name: string;
  conversionType: AdWithInsights["conversionType"];
  adset: string;
}> = [
  {
    name: "Summer Botox Campaign",
    conversionType: "message",
    adset: BOTOX_ADSET,
  },
  {
    name: "Clinic awareness ad",
    conversionType: "message",
    adset: COLLAB_ADSET,
  },
  {
    name: "Laser Hair Removal Bundle",
    conversionType: "message",
    adset: LASER_ADSET,
  },
  {
    name: "Lip Filler Awareness",
    conversionType: "form",
    adset: "Fillers - Broad",
  },
  {
    name: "Hydrafacial Spring Drive",
    conversionType: "website",
    adset: "Facials - Retargeting",
  },
  {
    name: "New Patient Welcome Offer",
    conversionType: "message",
    adset: BOTOX_ADSET,
  },
  {
    name: "Skin Boosters Launch",
    conversionType: "form",
    adset: "Fillers - Broad",
  },
];

/** Which campaign each seeded adset belongs to, so the picker can cascade. */
const ADSET_CAMPAIGNS: Record<string, string> = {
  [BOTOX_ADSET]: "Q3 Injectables - Messages",
  [LASER_ADSET]: "Q3 Laser - Messages",
  "Fillers - Broad": "Q3 Injectables - Messages",
  "Facials - Retargeting": "Q3 Skin - Traffic",
  [COLLAB_ADSET]: "Clinic awareness campaign",
};

export interface MockAdTargetNode {
  id: string;
  externalId: string;
  name: string;
}

const buildTargetNodes = (names: string[]): Map<string, MockAdTargetNode> =>
  new Map(
    names.map((name) => [
      name,
      {
        id: createId(),
        externalId: String(randomInt(10000000000, 99999999999)),
        name,
      },
    ]),
  );

export const MOCK_AD_CAMPAIGNS = buildTargetNodes([
  ...new Set(Object.values(ADSET_CAMPAIGNS)),
]);

export const MOCK_ADSETS = buildTargetNodes(Object.keys(ADSET_CAMPAIGNS));

/** Adset id → campaign id, for the cascading filter mocks. */
export const MOCK_ADSET_TO_CAMPAIGN = new Map(
  [...MOCK_ADSETS.entries()].map(([adsetName, adset]) => [
    adset.id,
    MOCK_AD_CAMPAIGNS.get(ADSET_CAMPAIGNS[adsetName])!.id,
  ]),
);

export function generateAdAccounts(
  organizationId: string,
  workspaceId: string,
): AdAccount[] {
  return [
    {
      id: createId(),
      externalId: `act_${randomInt(100000000, 999999999)}`,
      name: `${MARKET.organizationName} - Meta Ads`,
      status: AdAccountStatus.ACTIVE,
      lastSyncedAt: createPastTimestamp(1),
      currency: MARKET.currency,
      accountStatus: 1,
      tokenExpiresAt: null,
      organizationId,
      workspaceId,
      createdAt: createPastTimestamp(120),
      updatedAt: createPastTimestamp(1),
    },
  ];
}

function buildInsights(): AdInsights {
  const impressions = randomInt(5000, 80000);
  const clicks = randomInt(80, 2000);
  const spend = randomInt(500, 8000);

  return {
    currency: MARKET.currency,
    spend: String(spend),
    impressions: String(impressions),
    clicks: String(clicks),
    cpc: (spend / clicks).toFixed(2),
    cpm: ((spend / impressions) * 1000).toFixed(2),
  };
}

export function generateAds(
  organizationId: string,
  workspaceId: string,
  adAccountId: string | null,
): AdWithInsights[] {
  return AD_SEEDS.map((seed) => {
    const adset = MOCK_ADSETS.get(seed.adset)!;
    const campaign = MOCK_AD_CAMPAIGNS.get(ADSET_CAMPAIGNS[seed.adset])!;

    return {
      id: createId(),
      externalId: String(randomInt(10000000000, 99999999999)),
      name: seed.name,
      description: `Book your free consultation at ${MARKET.organizationName}.`,
      aiGeneratedDescription: null,
      videoUrl: null,
      imageUrl: `https://picsum.photos/seed/${encodeURIComponent(seed.name)}/600/400`,
      thumbnailUrl: `https://picsum.photos/seed/${encodeURIComponent(seed.name)}/120/120`,
      productId: null,
      source: "meta",
      mediaId: null,
      adAccountId,
      adsetId: adset.id,
      campaignId: campaign.id,
      adsetExternalId: adset.externalId,
      adsetName: seed.adset,
      campaignExternalId: campaign.externalId,
      campaignName: ADSET_CAMPAIGNS[seed.adset],
      status: pickRandom(["ACTIVE", "ACTIVE", "PAUSED"]),
      previewUrl: null,
      instagramPermalinkUrl: null,
      adInsights: buildInsights(),
      conversionType: seed.conversionType ?? pickRandom(CONVERSION_TYPES),
      organizationId,
      workspaceId,
      createdAt: createPastTimestamp(60),
      updatedAt: createPastTimestamp(2),
      attachedSummary: null,
    };
  });
}
